import React, { useMemo, useState, useCallback } from 'react';
import {
    ResponsiveContainer,
    Area,
    AreaChart,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    BarChart,
    Bar,
    Cell,
    ReferenceLine,
    LabelList,
} from 'recharts';
import { SLOT_IDX } from '../../core/Build';
import { getSlotName, getSlotImg } from '../../core/buildUtils';
import './GenerationSummaryPanel.css';

const TXT_PRECOMPUTE_SCORE_S_FORMATPROB_PR = { fr: 'Score de précalcul ${s} — ${formatProb(prob)} de chances d\'être tiré pour cet emplacement', en: 'Precompute score ${s} — ${formatProb(prob)} chance of being drawn for this slot', de: 'Vorberechnungs-Score ${s} — ${formatProb(prob)} Chance, für diesen Slot gezogen zu werden', es: 'Puntuación de precálculo ${s} — ${formatProb(prob)} de probabilidad de salir para este espacio', pt: 'Pontuação de pré-cálculo ${s} — ${formatProb(prob)} de chance de ser sorteado para este espaço' };
const TXT_BEST_SCORE = { fr: 'Meilleur score', en: 'Best score', de: 'Bester Score', es: 'Mejor puntuación', pt: 'Melhor pontuação' };
const TXT_NEW_BESTS = { fr: 'Nouveaux meilleurs', en: 'New bests', de: 'Neue Bestwerte', es: 'Nuevos mejores', pt: 'Novos melhores' };
const TXT_DUP_RINGS = { fr: 'Anneaux dup.', en: 'Dup. rings', de: 'Dupl. Ringe', es: 'Anillos dup.', pt: 'Anéis dup.' };
const TXT_DUP_TROPHIES = { fr: 'Trophées dup.', en: 'Dup. trophies', de: 'Dupl. Trophäen', es: 'Trofeos dup.', pt: 'Troféus dup.' };
const TXT_RESTRICTED_TROPHIES = { fr: 'Trophées restreints', en: 'Restricted trophies', de: 'Eingeschränkte Trophäen', es: 'Trofeos restringidos', pt: 'Troféus restritos' };
const TXT_AP_MP_RESTRICTED = { fr: 'AP/MP restreint', en: 'AP/MP restricted', de: 'AP/BP eingeschränkt', es: 'PA/PM restringido', pt: 'PA/PM restrito' };
const TXT_ITEM_LEVEL = { fr: 'Niveau d\'item', en: 'Item level', de: 'Gegenstandsstufe', es: 'Nivel del objeto', pt: 'Nível do item' };
const TXT_EXO_PLACEMENT = { fr: 'Exos sans objet libre', en: 'No item free for an exo', de: 'Kein freies Item für Exo', es: 'Ningún objeto libre para el exo', pt: 'Nenhum item livre para o exo' };
const TXT_ITEM_CRITERIA = { fr: 'Conditions d\'équipement', en: 'Equip conditions', de: 'Ausrüstbedingungen', es: 'Condiciones de equipamiento', pt: 'Condições de equipamento' };
const TXT_NO_INVALID_BUILDS_REJECTED = { fr: 'Aucun rejet d\'invalidité.', en: 'No invalid builds rejected.', de: 'Keine ungültigen Builds abgelehnt.', es: 'Ninguna build rechazada por invalidez.', pt: 'Nenhuma build rejeitada por invalidez.' };
const TXT_REJECTIONS = { fr: 'Rejets', en: 'Rejections', de: 'Ablehnungen', es: 'Rechazos', pt: 'Rejeições' };
const TXT_FINAL_SCORE = { fr: 'Score final', en: 'Final score', de: 'Endscore', es: 'Puntuación final', pt: 'Pontuação final' };
const TXT_AVAILABLE_WHEN_GENERATION_COMPLE = { fr: 'Disponible à la fin de la génération.', en: 'Available when generation completes.', de: 'Verfügbar, sobald die Generierung abgeschlossen ist.', es: 'Disponible al terminar la generación.', pt: 'Disponível ao terminar a geração.' };
const TXT_SCORE = { fr: 'Score', en: 'Score', de: 'Score', es: 'Puntuación', pt: 'Pontuação' };
const TXT_HARD = { fr: 'Cibles', en: 'Hard', de: 'Ziele', es: 'Objetivos', pt: 'Alvos' };
const TXT_ITEMS = { fr: 'Items', en: 'Items', de: 'Gegenstände', es: 'Objetos', pt: 'Itens' };
const TXT_LVL_ENTRY_LEVEL = { fr: 'lvl ${entry.level}', en: 'lvl ${entry.level}', de: 'St. ${entry.level}', es: 'niv. ${entry.level}', pt: 'nív. ${entry.level}' };
const TXT_ENTRY_PIECE_COUNT_PCS = { fr: '${entry.piece_count} pièces', en: '${entry.piece_count} pcs', de: '${entry.piece_count} Teile', es: '${entry.piece_count} piezas', pt: '${entry.piece_count} peças' };
const TXT_COMBO_SCORE_SCORE_PROB_SAMPLING = { fr: 'Score combo ${score} — ${prob} de tirage', en: 'Combo score ${score} — ${prob} sampling probability', de: 'Kombi-Score ${score} — ${prob} Ziehwahrscheinlichkeit', es: 'Puntuación de combo ${score} — ${prob} de probabilidad', pt: 'Pontuação de combo ${score} — ${prob} de probabilidade' };
const TXT_NO_CANDIDATES_AVAILABLE = { fr: 'Aucun candidat disponible.', en: 'No candidates available.', de: 'Keine Kandidaten verfügbar.', es: 'Ningún candidato disponible.', pt: 'Nenhum candidato disponível.' };
const TXT_NO_SET_CANDIDATES_AVAILABLE = { fr: 'Aucune panoplie candidate.', en: 'No set candidates available.', de: 'Keine Set-Kandidaten.', es: 'Ningún set candidato.', pt: 'Nenhum conjunto candidato.' };
const TXT_WAITING_FOR_DATA = { fr: 'En attente de données…', en: 'Waiting for data…', de: 'Warte auf Daten…', es: 'Esperando datos…', pt: 'Aguardando dados…' };
const TXT_VS_SEED = { fr: 'vs seed', en: 'vs seed', de: 'vs Seed', es: 'vs seed', pt: 'vs seed' };
const TXT_TIME_BEST = { fr: 'Temps → meilleur', en: 'Time → best', de: 'Zeit → Bestwert', es: 'Tiempo → mejor', pt: 'Tempo → melhor' };
const TXT_OF = { fr: 'sur', en: 'of', de: 'von', es: 'de', pt: 'de' };
const TXT_EVALUATIONS = { fr: 'Évaluations', en: 'Evaluations', de: 'Auswertungen', es: 'Evaluaciones', pt: 'Avaliações' };
const TXT_NEW_BESTS_2 = { fr: 'nouv. meilleurs', en: 'new bests', de: 'neue Bestw.', es: 'nuevos mejores', pt: 'novos melhores' };
const TXT_ACCEPTANCE = { fr: 'Acceptation', en: 'Acceptance', de: 'Annahme', es: 'Aceptación', pt: 'Aceitação' };
const TXT_CACHE_HIT = { fr: 'Cache', en: 'Cache hit', de: 'Cache-Treffer', es: 'Aciertos de caché', pt: 'Acertos de cache' };
const TXT_FIRST_GAIN = { fr: 'Premier gain', en: 'First gain', de: 'Erster Gewinn', es: 'Primera ganancia', pt: 'Primeiro ganho' };

const TXT_TITLE = { fr: 'Résumé de génération', en: 'Generation summary', de: 'Generierungs-Zusammenfassung', es: 'Resumen de generación', pt: 'Resumo da geração' };
const TXT_LIVE = { fr: 'En cours', en: 'Live', de: 'Läuft', es: 'En curso', pt: 'Em curso' };
const TXT_DONE = { fr: 'Terminé', en: 'Done', de: 'Fertig', es: 'Terminado', pt: 'Concluído' };

const SECTIONS = {
    convergence: {
        fr: 'Score dans le temps',
        en: 'Score over time',
        de: 'Score im Zeitverlauf',
        es: 'Puntuación a lo largo del tiempo',
        pt: 'Pontuação ao longo do tempo',
    },
    mutationMix: {
        fr: 'Productivité des mutations',
        en: 'Mutation productivity',
        de: 'Mutations-Produktivität',
        es: 'Productividad de las mutaciones',
        pt: 'Produtividade das mutações',
    },
    invalidReasons: {
        fr: 'Causes d\'invalidité',
        en: 'Invalid reasons',
        de: 'Ungültigkeitsgründe',
        es: 'Causas de invalidez',
        pt: 'Causas de invalidez',
    },
    workers: {
        fr: 'Résultats par worker',
        en: 'Per-worker results',
        de: 'Ergebnisse pro Worker',
        es: 'Resultados por worker',
        pt: 'Resultados por worker',
    },
    elite: { fr: 'Top builds', en: 'Top builds', de: 'Top-Builds', es: 'Top builds', pt: 'Top builds' },
    candidates: {
        fr: 'Candidats par emplacement',
        en: 'Candidates per slot',
        de: 'Kandidaten pro Slot',
        es: 'Candidatos por espacio',
        pt: 'Candidatos por espaço',
    },
    setCandidates: {
        fr: 'Panoplie candidates',
        en: 'Candidate sets',
        de: 'Kandidaten-Sets',
        es: 'Sets candidatos',
        pt: 'Conjuntos candidatos',
    },
};

const HINT = {
    convergence: {
        fr: 'Chaque palier = un nouveau meilleur',
        en: 'Each step = a new best build',
        de: 'Jede Stufe = ein neuer Bestwert',
        es: 'Cada escalón = una nueva mejor build',
        pt: 'Cada degrau = uma nova melhor build',
    },
    candidates: {
        fr: 'Meilleurs items par score de précalcul et probabilité de tirage',
        en: 'Top items by precompute score and slot sampling probability',
        de: 'Top-Gegenstände nach Vorberechnungs-Score und Ziehwahrscheinlichkeit',
        es: 'Mejores objetos por puntuación de precálculo y probabilidad de selección',
        pt: 'Melhores itens por pontuação de pré-cálculo e probabilidade de sorteio',
    },
    setCandidates: {
        fr: 'Meilleurs combos de swap de panoplie par score et probabilité de tirage',
        en: 'Top set-swap combos by precompute score and sampling probability',
        de: 'Top-Set-Swap-Kombis nach Score und Ziehwahrscheinlichkeit',
        es: 'Mejores combos de cambio de set por puntuación y probabilidad',
        pt: 'Melhores combos de troca de conjunto por pontuação e probabilidade',
    },
    mutationMix: {
        fr: 'Mutations ayant produit un nouveau meilleur',
        en: 'Mutations that produced a new best',
        de: 'Mutationen, die einen neuen Bestwert erzeugt haben',
        es: 'Mutaciones que han producido una nueva mejor',
        pt: 'Mutações que produziram um novo melhor',
    },
    invalidReasons: {
        fr: 'Ce qui a bloqué le solveur',
        en: 'What blocked the solver',
        de: 'Was den Solver blockiert hat',
        es: 'Lo que ha bloqueado el solver',
        pt: 'O que bloqueou o solver',
    },
    workers: {
        fr: 'Score final par worker',
        en: 'Final score per parallel worker',
        de: 'Endscore pro Worker',
        es: 'Puntuación final por worker',
        pt: 'Pontuação final por worker',
    },
};

const COLORS = {
    accent: '#3bff9d',
    accentSoft: 'rgba(59,255,157,0.18)',
    accentMid: 'rgba(59,255,157,0.55)',
    seed: 'rgba(255,255,255,0.35)',
    invalid: '#ff7676',
    invalidSoft: 'rgba(255,118,118,0.18)',
    newBest: '#ffd166',
    accepted: 'rgba(59,255,157,0.35)',
    axisText: 'rgba(255,255,255,0.45)',
    axisLine: 'rgba(255,255,255,0.15)',
    grid: 'rgba(255,255,255,0.07)',
};

const MUTATION_KEYS = ['slot', 'completion', 'trim', 'swap', 'set', 'rune'];
const MUTATION_LABELS = {
    slot: { fr: 'slot', en: 'slot', de: 'Slot', es: 'espacio', pt: 'espaço' },
    completion: { fr: 'compl.', en: 'compl.', de: 'Kompl.', es: 'compl.', pt: 'compl.' },
    trim: { fr: 'trim', en: 'trim', de: 'Trim', es: 'trim', pt: 'trim' },
    swap: { fr: 'swap', en: 'swap', de: 'Swap', es: 'swap', pt: 'swap' },
    set: { fr: 'set', en: 'set', de: 'Set', es: 'set', pt: 'conj.' },
    rune: { fr: 'rune', en: 'rune', de: 'Rune', es: 'runa', pt: 'runa' },
};

const ROWS = [
    { key: 'hat',      slotIdxs: [SLOT_IDX.HAT],                          labelSlot: SLOT_IDX.HAT },
    { key: 'cloak',    slotIdxs: [SLOT_IDX.CLOAK],                        labelSlot: SLOT_IDX.CLOAK },
    { key: 'amulet',   slotIdxs: [SLOT_IDX.AMULET],                       labelSlot: SLOT_IDX.AMULET },
    { key: 'rings',    slotIdxs: [SLOT_IDX.RING1, SLOT_IDX.RING2],        labelSlot: SLOT_IDX.RING1 },
    { key: 'belt',     slotIdxs: [SLOT_IDX.BELT],                         labelSlot: SLOT_IDX.BELT },
    { key: 'boots',    slotIdxs: [SLOT_IDX.BOOTS],                        labelSlot: SLOT_IDX.BOOTS },
    { key: 'weapon',   slotIdxs: [SLOT_IDX.WEAPON],                       labelSlot: SLOT_IDX.WEAPON },
    { key: 'shield',   slotIdxs: [SLOT_IDX.SHIELD],                       labelSlot: SLOT_IDX.SHIELD },
    { key: 'pet',      slotIdxs: [SLOT_IDX.PET],                          labelSlot: SLOT_IDX.PET },
    { key: 'trophies', slotIdxs: SLOT_IDX.TROPHUS,                        labelSlot: SLOT_IDX.PRYSMARADITE },
];

function tx(obj, lang) {
    return obj?.[lang] || obj?.en || '';
}

function fmtNum(n, digits = 1) {
    if (n == null || !Number.isFinite(n)) return '—';
    if (Math.abs(n) >= 10000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
    return Number(n).toFixed(digits);
}

function fmtPct(n, digits = 0) {
    if (n == null || !Number.isFinite(n)) return '—';
    return `${(n * 100).toFixed(digits)}%`;
}

function fmtSeconds(s) {
    if (s == null || !Number.isFinite(s)) return '—';
    if (s < 10) return `${s.toFixed(2)}s`;
    if (s < 60) return `${s.toFixed(1)}s`;
    return `${(s / 60).toFixed(1)}m`;
}

function fmtInt(n) {
    if (n == null || !Number.isFinite(n)) return '—';
    return Math.round(n).toLocaleString();
}

function fmtIntCompact(n) {
    if (n == null || !Number.isFinite(n)) return '—';
    const abs = Math.abs(n);
    if (abs >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
    return String(Math.round(n));
}

function getItemName(candidate, lang) {
    const item = candidate.item ?? candidate;
    if (!item) return `#${candidate.id ?? '?'}`;
    const name = item.name;
    if (!name) return `#${item.id ?? candidate.id ?? '?'}`;
    if (typeof name === 'object') return name[lang] || name.fr || name.en || `#${item.id}`;
    return name;
}

/** Sampling share is heavily skewed by the solver's probability exponent, so
 *  small values still matter — keep a digit rather than rounding them to 0%. */
function formatProb(prob) {
    const pct = prob * 100;
    if (pct >= 10) return `${Math.round(pct)}%`;
    if (pct >= 1) return `${pct.toFixed(1)}%`;
    if (pct >= 0.1) return `${pct.toFixed(2)}%`;
    return '<0.1%';
}

function probTitle(score, prob, lang) {
    if (prob == null) return undefined;
    const s = typeof score === 'number' ? score.toFixed(2) : score;
    return (TXT_PRECOMPUTE_SCORE_S_FORMATPROB_PR[lang] || TXT_PRECOMPUTE_SCORE_S_FORMATPROB_PR.en);
}

function getCandidateList(candidates, slotIdxs) {
    if (!candidates) return [];
    const seen = new Set();
    const merged = [];
    for (const slotIdx of slotIdxs) {
        const list = candidates[slotIdx] ?? candidates[String(slotIdx)] ?? [];
        for (const c of list) {
            const id = c.id ?? c;
            if (!seen.has(id)) {
                seen.add(id);
                merged.push(c);
            }
        }
    }
    return merged.sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
}

function ChartTooltip({ active, payload, label, valueFormatter, labelFormatter }) {
    if (!active || !payload || !payload.length) return null;
    return (
        <div className="gen-summary-tooltip">
            {label != null && (
                <div className="gen-summary-tooltip-label">
                    {labelFormatter ? labelFormatter(label) : label}
                </div>
            )}
            {payload.map((entry) => (
                <div key={entry.dataKey || entry.name} className="gen-summary-tooltip-row">
                    <span>
                        <span className="swatch" style={{ background: entry.color || entry.fill }} />
                        {entry.name}
                    </span>
                    <span>{valueFormatter ? valueFormatter(entry.value, entry) : entry.value}</span>
                </div>
            ))}
        </div>
    );
}

/**
 * One KPI. Renders nothing when there is no number.
 *
 * Not every engine measures every metric — the WASM kernel has no evaluation
 * cache at all — and a card reading "—" claims a measurement of zero rather
 * than the absence of one.
 */
function KpiCard({ label, value, sub, subClass, accent = false }) {
    if (value == null || value === '—') return null;
    return (
        <div className={`gen-summary-kpi${accent ? ' accent' : ''}`}>
            <div className="gen-summary-kpi-label">{label}</div>
            <div className="gen-summary-kpi-value">{value}</div>
            {sub != null && sub !== '' && (
                <div className={`gen-summary-kpi-sub${subClass ? ' ' + subClass : ''}`}>{sub}</div>
            )}
        </div>
    );
}

function Section({ titleObj, hintObj, lang, children }) {
    return (
        <div className="gen-summary-section">
            <h4 className="gen-summary-section-title">{tx(titleObj, lang)}</h4>
            <div className="gen-summary-section-content">
                {hintObj && (
                    <div className="gen-summary-section-hint">{tx(hintObj, lang)}</div>
                )}
                {children}
            </div>
        </div>
    );
}

function ConvergenceChart({ scoreTimeSeries, seedScore, timeLimit, elapsed, lang }) {
    const data = useMemo(() => {
        if (!Array.isArray(scoreTimeSeries) || scoreTimeSeries.length === 0) return [];
        const points = scoreTimeSeries.map((p) => ({
            t: Number(p.elapsed_s) || 0,
            score: Number(p.best_score) || 0,
        }));
        const last = points[points.length - 1];
        const plateauEnd = Math.max(
            Number.isFinite(timeLimit) ? timeLimit : 0,
            Number.isFinite(elapsed) ? elapsed : 0,
        );
        if (plateauEnd > last.t) {
            points.push({ t: plateauEnd, score: last.score });
        }
        return points;
    }, [scoreTimeSeries, timeLimit, elapsed]);

    if (data.length === 0) {
        return <EmptyHint lang={lang} />;
    }

    // Fitted to the curve alone. The curve starts at the seed when the run
    // reports one on the same scale; stretching the axis to an off-scale seed
    // flattened every real gain into a line.
    const minScore = Math.min(...data.map((d) => d.score));
    const maxScore = Math.max(...data.map((d) => d.score));
    const yPad = Math.max(1, (maxScore - minScore) * 0.1);
    const xMax = timeLimit && Number.isFinite(timeLimit) ? Math.max(timeLimit, data[data.length - 1].t) : 'dataMax';

    return (
        <div className="gen-summary-chart-wrap tall">
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 8, right: 14, bottom: 0, left: -10 }}>
                    <defs>
                        <linearGradient id="convergenceFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={COLORS.accent} stopOpacity={0.35} />
                            <stop offset="100%" stopColor={COLORS.accent} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke={COLORS.grid} />
                    <XAxis
                        type="number"
                        dataKey="t"
                        domain={[0, xMax]}
                        tickFormatter={(v) => `${v.toFixed(0)}s`}
                        stroke={COLORS.axisLine}
                        tick={{ fill: COLORS.axisText, fontSize: 10 }}
                        tickLine={false}
                        axisLine={false}
                    />
                    <YAxis
                        domain={[Math.floor(minScore - yPad), Math.ceil(maxScore + yPad)]}
                        stroke={COLORS.axisLine}
                        tick={{ fill: COLORS.axisText, fontSize: 10 }}
                        tickLine={false}
                        axisLine={false}
                        width={42}
                    />
                    <Tooltip
                        cursor={{ stroke: 'rgba(255,255,255,0.15)', strokeWidth: 1 }}
                        content={
                            <ChartTooltip
                                labelFormatter={(v) => `${Number(v).toFixed(2)}s`}
                                valueFormatter={(v) => Number(v).toFixed(2)}
                            />
                        }
                    />
                    {seedScore != null && (
                        <ReferenceLine
                            y={seedScore}
                            stroke={COLORS.seed}
                            strokeDasharray="3 3"
                            strokeWidth={1}
                            label={{
                                value: 'seed',
                                position: 'insideTopRight',
                                fill: COLORS.seed,
                                fontSize: 9,
                            }}
                        />
                    )}
                    <Area
                        type="stepAfter"
                        dataKey="score"
                        stroke={COLORS.accent}
                        strokeWidth={2}
                        fill="url(#convergenceFill)"
                        isAnimationActive={false}
                        name={TXT_BEST_SCORE[lang] || TXT_BEST_SCORE.en}
                        activeDot={{ r: 3, fill: COLORS.accent, stroke: '#000', strokeWidth: 1 }}
                        dot={false}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
}

function MutationMixChart({ metrics, lang }) {
    const data = useMemo(() => {
        if (!metrics) return [];
        const attempts = metrics.mutation_attempts || {};
        const newBest = metrics.mutation_new_best || {};
        const seedImprove = metrics.mutation_seed_improve || {};
        const accepted = metrics.mutation_accepted || {};
        const invalid = metrics.mutation_invalid || {};
        return MUTATION_KEYS.map((k) => ({
            type: MUTATION_LABELS[k]?.[lang] || MUTATION_LABELS[k]?.en || k,
            rawType: k,
            attempts: attempts[k] || 0,
            accepted: accepted[k] || 0,
            invalid: invalid[k] || 0,
            seedImprove: seedImprove[k] || 0,
            newBest: newBest[k] || 0,
        }));
    }, [metrics, lang]);

    if (data.every((d) => d.attempts === 0)) return <EmptyHint lang={lang} />;
    const maxNewBest = Math.max(...data.map((d) => d.newBest), 1);

    return (
        <div className="gen-summary-chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 16, right: 14, bottom: 0, left: -22 }} barCategoryGap="22%">
                    <CartesianGrid vertical={false} stroke={COLORS.grid} />
                    <XAxis
                        dataKey="type"
                        stroke={COLORS.axisLine}
                        tick={{ fill: COLORS.axisText, fontSize: 10 }}
                        tickLine={false}
                        axisLine={false}
                    />
                    <YAxis hide />
                    <Tooltip
                        cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                        content={
                            <ChartTooltip
                                valueFormatter={(v) => fmtInt(v)}
                            />
                        }
                    />
                    <Bar
                        dataKey="newBest"
                        name={TXT_NEW_BESTS[lang] || TXT_NEW_BESTS.en}
                        fill={COLORS.newBest}
                        radius={[3, 3, 0, 0]}
                    >
                        <LabelList
                            dataKey="newBest"
                            position="top"
                            formatter={(v) => (v > 0 ? v : '')}
                            fill={COLORS.axisText}
                            fontSize={10}
                        />
                        {data.map((d, i) => (
                            <Cell
                                key={i}
                                fill={d.newBest >= maxNewBest - 1e-6 ? COLORS.newBest : 'rgba(255,209,102,0.55)'}
                            />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

function InvalidReasonsChart({ metrics, lang }) {
    const data = useMemo(() => {
        if (!metrics?.invalid_reasons) return [];
        const labels = {
            dup_rings: TXT_DUP_RINGS[lang] || TXT_DUP_RINGS.en,
            dup_trophies: TXT_DUP_TROPHIES[lang] || TXT_DUP_TROPHIES.en,
            restricted_trophies: TXT_RESTRICTED_TROPHIES[lang] || TXT_RESTRICTED_TROPHIES.en,
            apmp_restricted: TXT_AP_MP_RESTRICTED[lang] || TXT_AP_MP_RESTRICTED.en,
            // The emitted key, not the Python constant's name — both engines
            // send `item_level`, so labelling `REJECT_ITEM_LEVEL` never matched
            // and the chart fell through to showing the raw key.
            item_level: TXT_ITEM_LEVEL[lang] || TXT_ITEM_LEVEL.en,
            item_criteria: TXT_ITEM_CRITERIA[lang] || TXT_ITEM_CRITERIA.en,
            exo_placement: TXT_EXO_PLACEMENT[lang] || TXT_EXO_PLACEMENT.en,
        };
        return Object.entries(metrics.invalid_reasons)
            .map(([k, v]) => ({ reason: labels[k] || k, count: Number(v) || 0 }))
            .filter((d) => d.count > 0)
            .sort((a, b) => b.count - a.count);
    }, [metrics, lang]);

    if (data.length === 0) {
        return (
            <div className="gen-summary-empty">
                {TXT_NO_INVALID_BUILDS_REJECTED[lang] || TXT_NO_INVALID_BUILDS_REJECTED.en}
            </div>
        );
    }

    return (
        <div className="gen-summary-chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} layout="vertical" margin={{ top: 4, right: 36, bottom: 0, left: 0 }}>
                    <CartesianGrid horizontal={false} stroke={COLORS.grid} />
                    <XAxis type="number" hide />
                    <YAxis
                        type="category"
                        dataKey="reason"
                        stroke={COLORS.axisLine}
                        tickLine={false}
                        axisLine={false}
                        width={132}
                        tick={{ fill: COLORS.axisText, fontSize: 10 }}
                    />
                    <Tooltip
                        cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                        content={<ChartTooltip valueFormatter={(v) => fmtInt(v)} />}
                    />
                    <Bar
                        dataKey="count"
                        name={TXT_REJECTIONS[lang] || TXT_REJECTIONS.en}
                        fill={COLORS.invalid}
                        fillOpacity={0.85}
                        radius={[0, 3, 3, 0]}
                    >
                        <LabelList
                            dataKey="count"
                            position="right"
                            formatter={(v) => fmtIntCompact(v)}
                            fill={COLORS.axisText}
                            fontSize={10}
                        />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

function WorkersChart({ workerMetrics, lang }) {
    const data = useMemo(() => {
        if (!Array.isArray(workerMetrics) || workerMetrics.length === 0) return [];
        return workerMetrics
            .map((wm) => ({
                worker: `#${wm.worker_index ?? '?'}`,
                score: Number(wm.final_score ?? wm.best_score ?? 0),
                evals: Number(wm.eval_count ?? 0),
            }))
            .sort((a, b) => b.score - a.score);
    }, [workerMetrics]);

    if (data.length <= 1) return null;
    const maxScore = Math.max(...data.map((d) => d.score));
    const minScore = Math.min(...data.map((d) => d.score));
    const pad = Math.max(1, (maxScore - minScore) * 0.15);

    return (
        <div className="gen-summary-chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 16, right: 14, bottom: 0, left: -22 }} barCategoryGap="28%">
                    <CartesianGrid vertical={false} stroke={COLORS.grid} />
                    <XAxis
                        dataKey="worker"
                        stroke={COLORS.axisLine}
                        tick={{ fill: COLORS.axisText, fontSize: 10 }}
                        tickLine={false}
                        axisLine={false}
                    />
                    <YAxis
                        domain={[Math.max(0, Math.floor(minScore - pad)), Math.ceil(maxScore + pad)]}
                        hide
                    />
                    <Tooltip
                        cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                        content={<ChartTooltip valueFormatter={(v) => fmtNum(v, 2)} />}
                    />
                    <Bar dataKey="score" name={TXT_FINAL_SCORE[lang] || TXT_FINAL_SCORE.en} radius={[3, 3, 0, 0]}>
                        <LabelList
                            dataKey="score"
                            position="top"
                            formatter={(v) => fmtNum(v, 1)}
                            fill={COLORS.axisText}
                            fontSize={10}
                        />
                        {data.map((d, i) => (
                            <Cell
                                key={i}
                                fill={d.score >= maxScore - 1e-6 ? COLORS.accent : COLORS.accentSoft}
                            />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

function ElitePoolTable({ elitePool, lang }) {
    if (!Array.isArray(elitePool) || elitePool.length === 0) {
        return (
            <div className="gen-summary-empty">
                {TXT_AVAILABLE_WHEN_GENERATION_COMPLE[lang] || TXT_AVAILABLE_WHEN_GENERATION_COMPLE.en}
            </div>
        );
    }
    const topScore = Math.max(...elitePool.map((b) => Number(b.score) || 0));
    return (
        <div className="gen-summary-elite-table">
            <div className="gen-summary-elite-row header">
                <div>#</div>
                <div>{TXT_SCORE[lang] || TXT_SCORE.en}</div>
                <div className="gen-summary-elite-delta-col">Δ</div>
                <div>{TXT_HARD[lang] || TXT_HARD.en}</div>
                <div>{TXT_ITEMS[lang] || TXT_ITEMS.en}</div>
            </div>
            {elitePool.map((b, i) => {
                const names = (b.item_names || [])
                    .map((n) => (typeof n === 'object' ? (n[lang] || n.fr || n.en || '') : n))
                    .filter(Boolean)
                    .join(' · ');
                const score = Number(b.score) || 0;
                const delta = score - topScore;
                const isTop = i === 0;
                return (
                    <div className={`gen-summary-elite-row${isTop ? ' is-top' : ''}`} key={i}>
                        <div className="gen-summary-elite-rank">{i + 1}</div>
                        <div className="gen-summary-elite-score">{fmtNum(score, 2)}</div>
                        <div className="gen-summary-elite-delta">
                            {isTop ? '' : `−${fmtNum(-delta, 2)}`}
                        </div>
                        <div className="gen-summary-elite-hard">{b.hard_score ?? '—'}</div>
                        <div className="gen-summary-elite-items" title={names}>{names}</div>
                    </div>
                );
            })}
        </div>
    );
}

function getSetName(entry, lang) {
    const name = entry?.name;
    if (!name) return `#${entry?.set_id ?? '?'}`;
    if (typeof name === 'object') return name[lang] || name.fr || name.en || `#${entry.set_id}`;
    return name;
}

function setTitle(entry, lang) {
    const parts = [];
    if (entry.level != null && entry.level > 0) {
        parts.push(TXT_LVL_ENTRY_LEVEL[lang] || TXT_LVL_ENTRY_LEVEL.en);
    }
    if (entry.piece_count != null) {
        parts.push(TXT_ENTRY_PIECE_COUNT_PCS[lang] || TXT_ENTRY_PIECE_COUNT_PCS.en);
    }
    const slotNames = Array.isArray(entry.slots)
        ? entry.slots.map((s) => getSlotName(s, lang)).filter(Boolean)
        : [];
    if (slotNames.length) parts.push(slotNames.join(' + '));
    const meta = parts.join(' · ');
    const items = Array.isArray(entry.items) && entry.items.length
        ? entry.items.join(' · ')
        : null;
    const score = entry.score != null ? Number(entry.score).toFixed(2) : '—';
    const prob = entry.prob != null ? formatProb(entry.prob) : '—';
    const head = TXT_COMBO_SCORE_SCORE_PROB_SAMPLING[lang] || TXT_COMBO_SCORE_SCORE_PROB_SAMPLING.en;
    return [head, meta, items].filter(Boolean).join('\n');
}

function CandidatesSection({ candidates, lang }) {
    const rows = ROWS.map((row) => ({
        ...row,
        list: getCandidateList(candidates, row.slotIdxs),
    })).filter((row) => row.list.length > 0);

    if (rows.length === 0) {
        return (
            <div className="gen-summary-empty">
                {TXT_NO_CANDIDATES_AVAILABLE[lang] || TXT_NO_CANDIDATES_AVAILABLE.en}
            </div>
        );
    }

    return (
        <div className="gen-summary-candidates">
            {rows.map((row) => {
                const slotName = getSlotName(row.labelSlot, lang);
                const slotImg = getSlotImg(row.labelSlot);
                return (
                    <div key={row.key} className="gen-summary-slot-row">
                        <div className="gen-summary-slot-label">
                            {slotImg && (
                                <img src={slotImg} alt={slotName} className="gen-summary-slot-icon" />
                            )}
                            <span className="gen-summary-slot-name">{slotName}</span>
                        </div>
                        <div className="gen-summary-slot-items">
                            {row.list.map((candidate, idx) => {
                                const name = getItemName(candidate, lang);
                                const score = candidate.score ?? candidate.item?.score;
                                const prob = candidate.prob ?? candidate.item?.prob;
                                return (
                                    <span
                                        key={candidate.id ?? candidate.item?.id ?? idx}
                                        className="gen-summary-item-chip"
                                        title={probTitle(score, prob, lang)}
                                    >
                                        <span className="gen-summary-item-name">{name}</span>
                                        {score != null && (
                                            <span className="gen-summary-item-score">
                                                {typeof score === 'number' ? score.toFixed(2) : score}
                                            </span>
                                        )}
                                        {prob != null && (
                                            <span className="gen-summary-item-prob">
                                                {formatProb(prob)}
                                            </span>
                                        )}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function SetCandidatesSection({ setCandidates, lang }) {
    const list = Array.isArray(setCandidates) ? setCandidates : [];
    if (list.length === 0) {
        return (
            <div className="gen-summary-empty">
                {TXT_NO_SET_CANDIDATES_AVAILABLE[lang] || TXT_NO_SET_CANDIDATES_AVAILABLE.en}
            </div>
        );
    }

    return (
        <div className="gen-summary-set-candidates">
            {list.map((entry, idx) => {
                const name = getSetName(entry, lang);
                const score = entry.score;
                const prob = entry.prob;
                const metaParts = [];
                if (entry.level != null && entry.level > 0) metaParts.push(`lvl ${entry.level}`);
                if (entry.piece_count != null) metaParts.push(`${entry.piece_count}pc`);
                const slotImgs = Array.isArray(entry.slots)
                    ? entry.slots.map((slotId) => ({
                        slotId,
                        src: getSlotImg(slotId),
                        label: getSlotName(slotId, lang),
                    })).filter((s) => s.src)
                    : [];
                return (
                    <div key={`${entry.set_id ?? name}-${idx}`} className="gen-summary-set-row">
                        <span
                            className="gen-summary-item-chip gen-summary-set-chip"
                            title={setTitle(entry, lang)}
                        >
                            <span className="gen-summary-item-name">{name}</span>
                            {slotImgs.length > 0 && (
                                <span className="gen-summary-set-slots" aria-hidden="true">
                                    {slotImgs.map((s, i) => (
                                        <img
                                            key={`${s.slotId}-${i}`}
                                            src={s.src}
                                            alt=""
                                            title={s.label}
                                            className="gen-summary-set-slot-icon"
                                        />
                                    ))}
                                </span>
                            )}
                            {metaParts.length > 0 && (
                                <span className="gen-summary-set-meta">{metaParts.join(' · ')}</span>
                            )}
                            {score != null && (
                                <span className="gen-summary-item-score">
                                    {typeof score === 'number' ? score.toFixed(2) : score}
                                </span>
                            )}
                            {prob != null && (
                                <span className="gen-summary-item-prob">{formatProb(prob)}</span>
                            )}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

function EmptyHint({ lang }) {
    return (
        <div className="gen-summary-empty">
            {TXT_WAITING_FOR_DATA[lang] || TXT_WAITING_FOR_DATA.en}
        </div>
    );
}

const GenerationSummaryPanel = ({
    lang = 'en',
    candidates,
    setCandidates,
    liveMetrics,
    finalMetrics,
    scoreTimeSeries,
    elitePool,
    workerMetrics,
    aggregateMetrics,
    isLive,
    open: openProp,
    onToggle,
}) => {
    // Whole-panel collapse — controlled when `open`/`onToggle` are passed,
    // otherwise falls back to local state (collapsed by default).
    const [openLocal, setOpenLocal] = useState(false);
    const open = openProp !== undefined ? openProp : openLocal;
    const toggle = useCallback(() => {
        if (onToggle) onToggle();
        else setOpenLocal((v) => !v);
    }, [onToggle]);

    // Prefer richer final metrics; fall back to live metrics during run
    const metrics = finalMetrics || liveMetrics || null;
    const elite = elitePool || [];

    const seedScore = metrics?.seed_score;
    const bestScore = metrics?.best_score;
    const deltaSeed =
        seedScore != null && bestScore != null ? bestScore - seedScore : null;

    const totalEvals = aggregateMetrics?.total_eval_count ?? metrics?.eval_count;
    const acceptance = metrics?.acceptance_rate;
    const cacheHit = metrics?.cache_hit_rate;
    const bestAt = metrics?.best_at_s;
    const elapsed = metrics?.elapsed_s;
    const firstImprovement = metrics?.first_improvement_s;
    const newBestCount = metrics?.new_best_updates;
    const timeLimit = metrics?.time_limit_s;

    const hasAnyData = metrics != null || candidates != null || setCandidates != null;
    if (!hasAnyData) return null;

    const summaryChips = [];
    if (bestScore != null) summaryChips.push({ key: 'best', label: fmtNum(bestScore, 1) });
    if (deltaSeed != null && deltaSeed !== 0) {
        summaryChips.push({
            key: 'delta',
            label: `${deltaSeed >= 0 ? '+' : ''}${fmtNum(deltaSeed, 1)}`,
            cls: deltaSeed >= 0 ? 'positive' : 'negative',
        });
    }
    if (totalEvals != null) summaryChips.push({ key: 'evals', label: `${fmtIntCompact(totalEvals)} eval` });

    return (
        <aside className={`gen-summary-panel panel${open ? ' is-open' : ' is-closed'}`}>
            <button
                type="button"
                className="gen-summary-header"
                onClick={toggle}
                aria-expanded={open}
            >
                <span className="gen-summary-header-left">
                    <span className={`gen-summary-status ${isLive ? 'live' : 'done'}`}>
                        <span className="gen-summary-status-dot" />
                        <span className="gen-summary-status-text">
                            {isLive ? tx(TXT_LIVE, lang) : tx(TXT_DONE, lang)}
                        </span>
                    </span>
                    <span className="gen-summary-title">{tx(TXT_TITLE, lang)}</span>
                </span>
                <span className="gen-summary-header-right">
                    {!open && summaryChips.length > 0 && (
                        <span className="gen-summary-summary-chips">
                            {summaryChips.map((c) => (
                                <span
                                    key={c.key}
                                    className={`gen-summary-summary-chip${c.cls ? ' ' + c.cls : ''}`}
                                >
                                    {c.label}
                                </span>
                            ))}
                        </span>
                    )}
                    <span className="gen-summary-chevron" aria-hidden="true">
                        {open ? '▾' : '▸'}
                    </span>
                </span>
            </button>

            {open && (
                <div className="gen-summary-body">
                    {/* KPI strip */}
                    <div className="gen-summary-kpi-grid">
                        <KpiCard
                            accent
                            label={TXT_BEST_SCORE[lang] || TXT_BEST_SCORE.en}
                            value={fmtNum(bestScore, 2)}
                            sub={
                                deltaSeed != null
                                    ? `${deltaSeed >= 0 ? '+' : ''}${fmtNum(deltaSeed, 1)} ${TXT_VS_SEED[lang] || TXT_VS_SEED.en}`
                                    : null
                            }
                            subClass={deltaSeed != null ? (deltaSeed >= 0 ? 'positive' : 'negative') : ''}
                        />
                        <KpiCard
                            label={TXT_TIME_BEST[lang] || TXT_TIME_BEST.en}
                            value={fmtSeconds(bestAt)}
                            sub={
                                elapsed != null
                                    ? `${TXT_OF[lang] || TXT_OF.en} ${fmtSeconds(elapsed)}`
                                    : null
                            }
                        />
                        <KpiCard
                            label={TXT_EVALUATIONS[lang] || TXT_EVALUATIONS.en}
                            value={fmtIntCompact(totalEvals)}
                            sub={
                                newBestCount != null
                                    ? `${newBestCount} ${TXT_NEW_BESTS_2[lang] || TXT_NEW_BESTS_2.en}`
                                    : null
                            }
                        />
                        <KpiCard
                            label={TXT_ACCEPTANCE[lang] || TXT_ACCEPTANCE.en}
                            value={fmtPct(acceptance)}
                        />
                        <KpiCard
                            label={TXT_CACHE_HIT[lang] || TXT_CACHE_HIT.en}
                            value={fmtPct(cacheHit)}
                        />
                        <KpiCard
                            label={TXT_FIRST_GAIN[lang] || TXT_FIRST_GAIN.en}
                            value={fmtSeconds(firstImprovement)}
                        />
                    </div>

                    <Section titleObj={SECTIONS.convergence} hintObj={HINT.convergence} lang={lang}>
                        <ConvergenceChart
                            scoreTimeSeries={scoreTimeSeries}
                            seedScore={seedScore}
                            timeLimit={timeLimit}
                            elapsed={elapsed}
                            lang={lang}
                        />
                    </Section>

                    <Section titleObj={SECTIONS.candidates} hintObj={HINT.candidates} lang={lang}>
                        <CandidatesSection candidates={candidates} lang={lang} />
                    </Section>

                    <Section titleObj={SECTIONS.setCandidates} hintObj={HINT.setCandidates} lang={lang}>
                        <SetCandidatesSection setCandidates={setCandidates} lang={lang} />
                    </Section>

                    <Section titleObj={SECTIONS.mutationMix} hintObj={HINT.mutationMix} lang={lang}>
                        <MutationMixChart metrics={aggregateMetrics || metrics} lang={lang} />
                    </Section>

                    <Section titleObj={SECTIONS.invalidReasons} hintObj={HINT.invalidReasons} lang={lang}>
                        <InvalidReasonsChart metrics={aggregateMetrics || metrics} lang={lang} />
                    </Section>

                    {Array.isArray(workerMetrics) && workerMetrics.length > 1 && (
                        <Section titleObj={SECTIONS.workers} hintObj={HINT.workers} lang={lang}>
                            <WorkersChart workerMetrics={workerMetrics} lang={lang} />
                        </Section>
                    )}

                    <Section titleObj={SECTIONS.elite} lang={lang}>
                        <ElitePoolTable elitePool={elite} lang={lang} />
                    </Section>
                </div>
            )}
        </aside>
    );
};

export default GenerationSummaryPanel;
